import { BodyPrimary, BodySecondary } from '@liveauctioneers/hammer-ui-core/text';
import { cn } from '@liveauctioneers/hammer-ui-theme/cn';
import { PerformanceLog } from '@/redux/modules/apiPerformanceStats';
import { useIntl } from '@liveauctioneers/hammer-ui-core/intl';
import Link from '@liveauctioneers/caterwaul-components/lib/Link/Link';

type ApiStatsTableProps = {
    className?: string;
    client: PerformanceLog | null;
    server: PerformanceLog | null;
    type: 'client' | 'server';
};

type ApiStatsRow = {
    error: boolean;
    isDupedApi: boolean;
    isDupedApiOnClientFromServer: boolean;
    key: string;
    method: string;
    notGood: boolean;
    notGreat: boolean;
    number: number;
    status: number;
    terrible: boolean;
    totalTime: number;
    url: string;
    waterfall: number;
};

const getRowClassName = (row: ApiStatsRow) => {
    if (row.terrible) {
        return 'bg-critical-100';
    }
    if (row.notGood) {
        return 'bg-critical-50';
    }
    if (row.notGreat) {
        return 'bg-warning-100';
    }
    return 'bg-success-100';
};

const ApiStatsTable = ({ className, client, server, type }: ApiStatsTableProps) => {
    const intl = useIntl();

    if ((type === 'client' && !client) || (type === 'server' && !server)) {
        return <BodyPrimary>loading {type}...</BodyPrimary>;
    }

    const apiLog = type === 'client' ? client : server;

    const data: ApiStatsRow[] =
        apiLog?.stats.requests.map((request, i) => {
            const isDupedApi = apiLog.stats.requests.filter((req) => req.url === request.url).length > 1;
            const isDupedApiOnClientFromServer =
                type === 'client' && (server?.stats.requests.filter((req) => req.url === request.url).length ?? 0) >= 1;
            const notGreat = request.totalTime > 100;
            const notGood = request.totalTime > 200;
            const terrible = request.totalTime > 300;

            return {
                error: Boolean(request.error),
                isDupedApi,
                isDupedApiOnClientFromServer,
                key: `${request.url}-${i}`,
                method: request.method,
                notGood,
                notGreat,
                number: i + 1,
                status: request.status,
                terrible,
                totalTime: request.totalTime,
                url: request.url,
                waterfall: request.waterfall,
            };
        }) ?? [];

    return (
        <table className={cn('w-full border-collapse', className)}>
            <thead>
                <tr className="border-b border-border-primary">
                    <th
                        className="pb-xs text-left uppercase"
                        scope="col"
                    >
                        <BodySecondary>
                            {intl.formatMessage({ id: 'healthcheck.tableHeaders.apiStatsTable.number' })}
                        </BodySecondary>
                    </th>
                    <th
                        className="pb-xs text-left uppercase"
                        scope="col"
                    >
                        <BodySecondary>
                            {intl.formatMessage({ id: 'healthcheck.tableHeaders.apiStatsTable.method' })}
                        </BodySecondary>
                    </th>
                    <th
                        className="pb-xs text-left uppercase"
                        scope="col"
                    >
                        <BodySecondary>
                            {intl.formatMessage({ id: 'healthcheck.tableHeaders.apiStatsTable.status' })}
                        </BodySecondary>
                    </th>
                    <th
                        className="pb-xs text-left uppercase"
                        scope="col"
                    >
                        <BodySecondary>
                            {intl.formatMessage({ id: 'healthcheck.tableHeaders.apiStatsTable.apiUrl' })}
                        </BodySecondary>
                    </th>
                    <th
                        className="pb-xs text-left uppercase"
                        scope="col"
                    >
                        <BodySecondary>
                            {intl.formatMessage({ id: 'healthcheck.tableHeaders.apiStatsTable.time' })}
                        </BodySecondary>
                    </th>
                    <th
                        className="pb-xs text-left uppercase"
                        scope="col"
                    >
                        <BodySecondary>
                            {intl.formatMessage({ id: 'healthcheck.tableHeaders.apiStatsTable.waterfall' })}
                        </BodySecondary>
                    </th>
                </tr>
            </thead>
            <tbody>
                {data.map((row) => (
                    <tr
                        className={cn('border-b border-border-primary last:border-b-0', getRowClassName(row))}
                        key={row.key}
                    >
                        <td className="py-sm">
                            <BodyPrimary>{row.number}</BodyPrimary>
                        </td>
                        <td className="py-sm">
                            <BodyPrimary>{row.method}</BodyPrimary>
                        </td>
                        <td className="py-sm">
                            <BodyPrimary>{row.status}</BodyPrimary>
                        </td>
                        <td className="max-w-[250px] whitespace-normal break-words py-sm">
                            <Link
                                rel="noopener"
                                target="_blank"
                                to={row.url}
                            >
                                {row.url}
                            </Link>
                            {row.isDupedApi && (
                                <BodyPrimary
                                    className="hui-text-h4 block"
                                    color="critical"
                                >
                                    (duped {type} api)
                                </BodyPrimary>
                            )}
                            {row.isDupedApiOnClientFromServer && (
                                <BodyPrimary
                                    className="hui-text-h4 block"
                                    color="critical"
                                >
                                    (duped client api from server)
                                </BodyPrimary>
                            )}
                        </td>
                        <td className="py-sm">
                            <BodyPrimary>{Math.round(row.totalTime)}ms</BodyPrimary>
                        </td>
                        <td className="py-sm">
                            <BodyPrimary>{row.waterfall}</BodyPrimary>
                        </td>
                    </tr>
                ))}
            </tbody>
        </table>
    );
};

export default ApiStatsTable;
